// Package imports:
import React from 'react';
// Component imports:
import ColoredNumber from '../../ui-elements/ColoredNumber/ColoredNumber';
import Loading from '../../ui-elements/Loading/Loading';
import DisplayBox from '../DisplayBox/DisplayBox';
import PriceRange from '../../ui-elements/PriceRange/PriceRange';
import TandemTable from '../../ui-elements/Table/TandemTable';
import Flag from '../../ui-elements/Flag/Flag';
import Link from '../../ui-elements/Link/Link';
import Datepicker from '../DatePicker/DatePicker';
import ErrorAlert from '../ErrorAlert/ErrorAlert';
// Service imports:
import { formatNumber, convertToPercentage, getAPItoday } from '../../services/utils';
import { useApiLmdDataMappedByString } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdIndexStatisticList } from '../../types/MarketTypes';

const firstTableSymbols = [ 'OMXN40', 'OMXC25', 'OMXS30', 'OMXH25' ];
const secondTableSymbols = [ 'OMXI10GI', 'OMXI10CAP', 'OMXI10', 'OMXIGI', 'OMXIPI' ];
const thirdTableSymbols = [ 'NOMXIBB', 'NOMXIBBTA', 'NOMXINOM', 'NOMXINOMTA' ];
const fourthTableSymbols = [ 'NOMXIREAL', 'NOMXIREALTA' ];
const indexesToShow = [...firstTableSymbols, ...secondTableSymbols, ...thirdTableSymbols, ...fourthTableSymbols ];
const ISKFLAG = <Flag name='ISK' />;


interface IOwnProps {
    basecurrency?: string
}
type Props = IDefaultProps & IOwnProps & React.HTMLAttributes<HTMLDivElement>;

const MultipleIndices: React.FC<Props> = ({
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [ date, setDate, indexStatistics ] = useApiLmdDataMappedByString<IApiLmdIndexStatisticList[]>(
        getAPItoday(),
        (selectedDate) => `/v1/market_data/v1/indexes/symbol/[${indexesToShow.join(';')}]/index_statistics_list?trading_date=${selectedDate}`,
        accessToken,
        refreshRateMs
    );

    const nameChanger = (symbol: string) => {
        if (symbol === 'OMXN40') {
            return (
                <>
                    <span className="flag">
                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                            <Flag name='OMX'/>
                        </a>
                    </span>
                    <span className="symbol">
                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>OMXN40 - Nordic</Link>
                    </span>
                </>
            );
        }
        if (symbol === 'OMXC25') {
            return (
                <>
                    <span className="flag">
                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                            <Flag name='DKK' />
                        </a>
                    </span>
                    <span className="symbol">
                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>OMXC25 - Danmörk</Link>
                    </span>
                </>
            );
        }
        if (symbol ===  'OMXS30') {
            return (
                <>
                    <span className="flag">
                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                            <Flag name='SEK' />
                        </a>
                    </span>
                    <span className="symbol">
                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>OMXS30 - Svíþjóð</Link>
                    </span>
                </>
            );
        }
        if (symbol ===  'OMXH25') {
            return (
                <>
                    <span className="flag">
                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                            <Flag name='FIN' />
                        </a>
                    </span>
                    <span className="symbol">
                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>OMXH25 - Finnland</Link>
                    </span>
                </>
            );
        }
    }

    const { data, error } = indexStatistics;
    return (
        <DisplayBox 
            className='KCL_multipleIndices'
            title="Helstu vísitölur"
            asideComponent={
                <Datepicker 
                    maxDate={new Date()}
                    size="sm"
                    selectedDate={new Date(date)}
                    setSelectedDate={(newDate) => setDate(getAPItoday(newDate))}
                />
            }
            {...props}
        >
            {(data === null) 
                ? (
                    (error === null)
                    ? <Loading />
                    : <ErrorAlert error={error} />
                )
                : <TandemTable 
                    data={[
                        data.filter(x => firstTableSymbols.includes(x.symbol)),
                        data.filter(x => secondTableSymbols.includes(x.symbol)),
                        data.filter(x => thirdTableSymbols.includes(x.symbol)),
                        data.filter(x => fourthTableSymbols.includes(x.symbol)),
                    ]}
                    columns={[
                        //TABLE ONE 
                        // Norrænar hlutabréfavísitölur
                        [{
                            title: <span className='table-section__title'>Norrænar hlutabréfavísitölur</span>,
                            dataAccessor: ( {symbol} ) => (
                                <div className="flag-and-symbol">
                                    {nameChanger(symbol)}
                                </div>
                                ),
                            textAlign: 'left'
                        }, 
                        {
                            title: 'Gildi',
                            dataAccessor: ( {last_value} ) => (formatNumber(last_value, '-', 2)),
                        },
                        {
                            title: 'Dagsbreyting',
                            dataAccessor: ( {yesterdays_value_change_per} ) => (
                                <ColoredNumber  
                                    sign={(yesterdays_value_change_per === null) ? 'neutral' : (yesterdays_value_change_per < 0) ? 'neg' : (yesterdays_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(yesterdays_value_change_per, true, 2)}
                                </ColoredNumber>
                            ),
                            
                        }, {
                            title: '1 Mán.',
                            dataAccessor: ( {weeks4_value_change_per} ) => (
                            <ColoredNumber  
                                sign={(weeks4_value_change_per === null) ? 'neutral' : (weeks4_value_change_per < 0) ? 'neg' : (weeks4_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(weeks4_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: 'Innan árs',
                            dataAccessor: ( {year_value_change_per}) => (
                                <ColoredNumber  
                                sign={(year_value_change_per === null) ? 'neutral' : (year_value_change_per < 0) ? 'neg' : (year_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(year_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '1 Ár',
                            dataAccessor: ( { months12_value_change_per } ) => (
                                <ColoredNumber  
                                    sign={(months12_value_change_per === null) ? 'neutral' : (months12_value_change_per < 0) ? 'neg' : (months12_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(months12_value_change_per, true, 2)}
                                </ColoredNumber>),
                        }, {
                            title: 'Árs bil',
                            dataAccessor: ( { months12_low_value, months12_high_value, last_value } ) => (
                                <PriceRange low={months12_low_value} high={months12_high_value} value={last_value} />),
                        }],
                        //TABLE TWO
                        //Íslenskar hlutabréfavísitölur
                        [{
                            title: <span className='table-section__title'>Íslenskar hlutabréfavísitölur</span>,
                            dataAccessor: ( {symbol} ) => (
                                <div className="flag-and-symbol">
                                    <span className="flag">
                                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                                            {ISKFLAG}
                                        </a>
                                    </span>
                                    <span className="symbol">
                                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>{symbol}</Link>
                                    </span>
                                </div>
                            ),
                            textAlign: 'left'
                        }, 
                        {
                            title: '',
                            dataAccessor: ( {last_value} ) => (formatNumber(last_value, '-', 2)),
                        },
                        {
                            title: '',
                            dataAccessor: ( {yesterdays_value_change_per} ) => (
                                <ColoredNumber  
                                    sign={(yesterdays_value_change_per === null) ? 'neutral' : (yesterdays_value_change_per < 0) ? 'neg' : (yesterdays_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(yesterdays_value_change_per, true, 2)}
                                </ColoredNumber>
                            ),
                            
                        }, {
                            title: '',
                            dataAccessor: ( {weeks4_value_change_per} ) => (
                            <ColoredNumber  
                                sign={(weeks4_value_change_per === null) ? 'neutral' : (weeks4_value_change_per < 0) ? 'neg' : (weeks4_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(weeks4_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( {year_value_change_per  }) => (
                                <ColoredNumber  
                                sign={(year_value_change_per === null) ? 'neutral' : (year_value_change_per < 0) ? 'neg' : (year_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(year_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_value_change_per } ) => (
                                <ColoredNumber  
                                    sign={(months12_value_change_per === null) ? 'neutral' : (months12_value_change_per < 0) ? 'neg' : (months12_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(months12_value_change_per, true, 2)}
                                </ColoredNumber>),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_low_value, months12_high_value, last_value } ) => (
                                <PriceRange low={months12_low_value} high={months12_high_value} value={last_value} />),
                        }],
                        //TABLE THREE
                        //Íslenskar óverðtryggðar skuldabréfavísitölur
                        [{
                            title: <span className='table-section__title'>Íslenskar óverðtryggðar skuldabréfavísitölur</span>,
                            dataAccessor: ( {symbol} ) => (
                                <div className="flag-and-symbol">
                                    <span className="flag">
                                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                                            {ISKFLAG}
                                        </a>
                                    </span>
                                    <span className="symbol">
                                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>{symbol}</Link>
                                    </span>
                                </div>
                            ),
                            textAlign: 'left'
                        }, 
                        {
                            title: '',
                            dataAccessor: ( {last_value} ) => (formatNumber(last_value, '-', 2)),
                        },
                        {
                            title: '',
                            dataAccessor: ( {yesterdays_value_change_per} ) => (
                                <ColoredNumber  
                                    sign={(yesterdays_value_change_per === null) ? 'neutral' : (yesterdays_value_change_per < 0) ? 'neg' : (yesterdays_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(yesterdays_value_change_per, true, 2)}
                                </ColoredNumber>
                            ),
                            
                        }, {
                            title: '',
                            dataAccessor: ( {weeks4_value_change_per} ) => (
                            <ColoredNumber  
                                sign={(weeks4_value_change_per === null) ? 'neutral' : (weeks4_value_change_per < 0) ? 'neg' : (weeks4_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(weeks4_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( {year_value_change_per}) => (
                                <ColoredNumber  
                                sign={(year_value_change_per === null) ? 'neutral' : (year_value_change_per < 0) ? 'neg' : (year_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(year_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_value_change_per } ) => (
                                <ColoredNumber  
                                    sign={(months12_value_change_per === null) ? 'neutral' : (months12_value_change_per < 0) ? 'neg' : (months12_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(months12_value_change_per, true, 2)}
                                </ColoredNumber>),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_low_value, months12_high_value, last_value } ) => (
                                <PriceRange low={months12_low_value} high={months12_high_value} value={last_value} />),
                        }],
                        //TABLE FOUR
                        //Íslenskar verðtryggðar skuldabréfavísitölur
                        [{
                            title: <span className='table-section__title'>Íslenskar verðtryggðar skuldabréfavísitölur</span>,
                            dataAccessor: ( {symbol} ) => (
                                <div className="flag-and-symbol">
                                    <span className="flag">
                                        <a href={`/Markadir/Visitolur/${symbol}`} aria-label={`Vísitölur - ${symbol}`}>
                                            {ISKFLAG}
                                        </a>
                                    </span>
                                    <span className="symbol">
                                        <Link url={`/Markadir/Visitolur/${symbol}`} linkSize='15'>{symbol}</Link>
                                    </span>
                                </div>
                            ),
                            textAlign: 'left'
                        }, 
                        {
                            title: '',
                            dataAccessor: ( {last_value} ) => (formatNumber(last_value, '-', 2)),
                        },
                        {
                            title: '',
                            dataAccessor: ( {yesterdays_value_change_per} ) => (
                                <ColoredNumber  
                                    sign={(yesterdays_value_change_per === null) ? 'neutral' : (yesterdays_value_change_per < 0) ? 'neg' : (yesterdays_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(yesterdays_value_change_per, true, 2)}
                                </ColoredNumber>
                            ),
                            
                        }, {
                            title: '',
                            dataAccessor: ( {weeks4_value_change_per} ) => (
                            <ColoredNumber  
                                sign={(weeks4_value_change_per === null) ? 'neutral' : (weeks4_value_change_per < 0) ? 'neg' : (weeks4_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(weeks4_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( {year_value_change_per  }) => (
                                <ColoredNumber  
                                sign={(year_value_change_per === null) ? 'neutral' : (year_value_change_per < 0) ? 'neg' : (year_value_change_per > 0) ? 'pos' : 'neutral'}>
                                    {convertToPercentage(year_value_change_per, true, 2)}
                            </ColoredNumber>
                            ),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_value_change_per } ) => (
                                <ColoredNumber  
                                    sign={(months12_value_change_per === null) ? 'neutral' : (months12_value_change_per < 0) ? 'neg' : (months12_value_change_per > 0) ? 'pos' : 'neutral'}>
                                        {convertToPercentage(months12_value_change_per, true, 2)}
                                </ColoredNumber>),
                        }, {
                            title: '',
                            dataAccessor: ( { months12_low_value, months12_high_value, last_value } ) => (
                                <PriceRange low={months12_low_value} high={months12_high_value} value={last_value} />),
                        }],
                    ]}
                    tableSize='lg'
                />
            }
        </DisplayBox>
    );
}

export default MultipleIndices;